import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import {
    Box,
    Paper,
    Stack,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    CircularProgress,
    Button,
    Snackbar,
    Alert,
    Chip,
    TextField,
    TablePagination,
    MenuItem,
    Collapse,
} from '@mui/material';
import {useTheme} from '@mui/material/styles';
import {GlCategoryService} from '@/services/accountService';
import GlCategoryDetail from '@/pages/components/manage/category/GlCategoryDetail';
import {
    flattenTree,
    getVisibleTreeRows,
    treeLeadingCellSx,
} from '@/utils/treeTableHelpers';
import {TreeExpandCell} from '@/utils/TreeExpandCell';
import {usePageCrud} from '@/hooks/usePageCrud';
import {
    buildGlCategoryExportFileName,
    exportGlCategoriesToXlsx,
    parseGlCategoriesFromXlsx,
} from '@/pages/components/manage/category/glCategoryExcel';

type GlCategoryTreeNode = GlCategoryService.GlCategory & { children?: GlCategoryTreeNode[] };

const typeColor = (t?: string) => {
    switch (t) {
        case 'ASSET':
            return 'success';
        case 'LIABILITY':
            return 'warning';
        case 'EQUITY':
            return 'info';
        case 'REVENUE':
            return 'secondary';
        case 'EXPENSE':
            return 'error';
        case 'ADJUSTMENT':
            return 'default';
        default:
            return 'default';
    }
};

function buildTreeFromFlat(flat: GlCategoryService.GlCategory[]): GlCategoryTreeNode[] {
    const map = new Map<number, GlCategoryTreeNode>();
    for (const c of flat) {
        if (c.categoryId == null) continue;
        map.set(c.categoryId, {...c, children: []});
    }
    const roots: GlCategoryTreeNode[] = [];
    map.forEach((node) => {
        const pid = node.parentId;
        if (pid != null && map.has(pid)) {
            map.get(pid)!.children!.push(node);
        } else {
            roots.push(node);
        }
    });
    const sortRec = (nodes: GlCategoryTreeNode[]) => {
        nodes.sort((a, b) => (a.code || '').localeCompare(b.code || ''));
        nodes.forEach((n) => {
            if (n.children?.length) sortRec(n.children);
        });
    };
    sortRec(roots);
    return roots;
}

function nodeMatches(
    node: GlCategoryTreeNode,
    code: string,
    nameEn: string,
    nameZh: string,
    type: string,
): boolean {
    const c = code.trim().toLowerCase();
    const en = nameEn.trim().toLowerCase();
    const zh = nameZh.trim();
    if (c && !(node.code || '').toLowerCase().includes(c)) return false;
    if (en && !(node.nameEn || '').toLowerCase().includes(en)) return false;
    if (zh && !(node.nameZh || '').includes(zh)) return false;
    if (type && (node.type || '') !== type) return false;
    return true;
}

/** 保留命中节点及其祖先；子树只保留命中分支 */
function filterTree(
    nodes: GlCategoryTreeNode[],
    code: string,
    nameEn: string,
    nameZh: string,
    type: string,
): GlCategoryTreeNode[] {
    const hasFilter = !!(code.trim() || nameEn.trim() || nameZh.trim() || type);
    if (!hasFilter) return nodes;

    const out: GlCategoryTreeNode[] = [];
    for (const n of nodes) {
        const kids = filterTree(n.children || [], code, nameEn, nameZh, type);
        if (nodeMatches(n, code, nameEn, nameZh, type) || kids.length > 0) {
            out.push({
                ...n,
                children: kids.length > 0
                    ? kids
                    : (nodeMatches(n, code, nameEn, nameZh, type) ? (n.children || []) : []),
            });
        }
    }
    return out;
}

function collectExpandIds(nodes: GlCategoryTreeNode[]): number[] {
    const ids: number[] = [];
    const walk = (list: GlCategoryTreeNode[]) => {
        for (const n of list) {
            if (n.children && n.children.length > 0 && n.categoryId != null) {
                ids.push(n.categoryId);
                walk(n.children);
            }
        }
    };
    walk(nodes);
    return ids;
}

const GlCategoryList: React.FC = () => {
    const theme = useTheme();
    const [fullTree, setFullTree] = useState<GlCategoryTreeNode[]>([]);
    const [loading, setLoading] = useState<boolean>(true);
    const [error, setError] = useState<string | null>(null);
    const [snackbar, setSnackbar] = useState<{ open: boolean; message: string; severity: 'success' | 'error' }>(
        {open: false, message: '', severity: 'success'},
    );
    const [filterCode, setFilterCode] = useState('');
    const [filterENName, setFilterENName] = useState('');
    const [filterZHName, setFilterZHName] = useState('');
    const [filterType, setFilterType] = useState<string>('');
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(25);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editId, setEditId] = useState<number | undefined>(undefined);
    const [openRowIds, setOpenRowIds] = useState<number[]>([]);
    const [importing, setImporting] = useState(false);
    const [exporting, setExporting] = useState(false);
    const importInputRef = useRef<HTMLInputElement>(null);
    const {canRead, canCreate, canUpdate, canRemove} = usePageCrud({
        read: ['gl_category_list', 'gl_category_get', 'gl_category_query_tree', 'gl_category_query_page_tree'],
        create: 'account:gl_category:create',
        update: 'account:gl_category:update',
        remove: 'account:gl_category:delete',
    });

    const fetchData = useCallback(async () => {
        setLoading(true);
        try {
            const all = await GlCategoryService.listAllCategories();
            setFullTree(buildTreeFromFlat(all || []));
            setError(null);
        } catch (e: any) {
            const msg = e?.message || 'Load failed';
            setError(msg);
            setSnackbar({open: true, message: msg, severity: 'error'});
        } finally {
            setLoading(false);
        }
    }, []);

    useEffect(() => {
        fetchData();
    }, [fetchData]);

    const filteredTree = useMemo(
        () => filterTree(fullTree, filterCode, filterENName, filterZHName, filterType),
        [fullTree, filterCode, filterENName, filterZHName, filterType],
    );

    const hasActiveFilter = !!(
        filterCode.trim() || filterENName.trim() || filterZHName.trim() || filterType
    );

    // 有筛选时展开命中路径，方便看到叶子
    useEffect(() => {
        if (hasActiveFilter) {
            setOpenRowIds(collectExpandIds(filteredTree));
        }
    }, [hasActiveFilter, filteredTree]);

    const total = filteredTree.length;
    const pagedRoots = useMemo(
        () => filteredTree.slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage),
        [filteredTree, page, rowsPerPage],
    );

    // 筛选条件变化时回到第一页
    useEffect(() => {
        setPage(0);
    }, [filterCode, filterENName, filterZHName, filterType]);

    const handleDelete = async (id?: number) => {
        if (!id) return;
        try {
            await GlCategoryService.deleteCategory(id);
            setSnackbar({open: true, message: 'Deleted', severity: 'success'});
            fetchData();
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Delete failed', severity: 'error'});
        }
    };

    const handleExport = async () => {
        setExporting(true);
        try {
            const all = await GlCategoryService.listAllCategories();
            if (!all.length) {
                setSnackbar({open: true, message: 'No categories to export', severity: 'error'});
                return;
            }
            exportGlCategoriesToXlsx(all, buildGlCategoryExportFileName());
            setSnackbar({
                open: true,
                message: `Exported ${all.length} categor${all.length === 1 ? 'y' : 'ies'}`,
                severity: 'success',
            });
        } catch (e: any) {
            setSnackbar({open: true, message: e?.message || 'Export failed', severity: 'error'});
        } finally {
            setExporting(false);
        }
    };

    const handleImportClick = () => {
        importInputRef.current?.click();
    };

    const handleImportFile = async (e: React.ChangeEvent<HTMLInputElement>) => {
        const file = e.target.files?.[0];
        e.target.value = '';
        if (!file) return;

        setImporting(true);
        try {
            const {rows: parsed, skippedEmpty} = await parseGlCategoriesFromXlsx(file);
            if (parsed.length === 0) {
                setSnackbar({
                    open: true,
                    message: skippedEmpty
                        ? `No valid rows (skipped ${skippedEmpty} empty)`
                        : 'No rows found in file',
                    severity: 'error',
                });
                return;
            }

            const msg = [
                `Import ${file.name}:`,
                `${parsed.length} row(s)`,
                skippedEmpty ? `${skippedEmpty} empty skipped in file` : null,
            ].filter(Boolean).join(' · ');
            if (!window.confirm(`${msg}\n\nBatch upsert by code now?`)) return;

            const result = await GlCategoryService.batchUpsertCategories(parsed);
            const created = result?.created ?? 0;
            const updated = result?.updated ?? 0;
            const skipped = (result?.skipped ?? 0) + skippedEmpty;
            const failed = result?.failed ?? 0;
            setSnackbar({
                open: true,
                message: `Imported: ${created} created, ${updated} updated`
                    + (skipped ? `, ${skipped} skipped` : '')
                    + (failed ? `, ${failed} failed` : ''),
                severity: failed ? 'error' : 'success',
            });
            await fetchData();
        } catch (err: any) {
            setSnackbar({open: true, message: err?.message || 'Import failed', severity: 'error'});
        } finally {
            setImporting(false);
        }
    };

    const handleToggleRow = (id: number) => {
        setOpenRowIds((prev) => prev.includes(id) ? prev.filter(i => i !== id) : [...prev, id]);
    };

    const getRowId = (node: GlCategoryTreeNode) => node.categoryId!;

    if (loading) return <CircularProgress sx={{display: 'block', mx: 'auto', my: 4}}/>;
    if (error) return <Typography color="error">{error}</Typography>;

    return (
        <Box sx={{
            p: 2,
            bgcolor: 'transparent',
            height: 'calc(100vh - 120px)',
            minHeight: '70vh',
            boxSizing: 'border-box',
            display: 'flex',
            flexDirection: 'column',
            overflow: 'hidden',
        }}>
            <Stack direction={{xs: 'column', sm: 'row'}} justifyContent="space-between"
                   alignItems={{xs: 'stretch', sm: 'center'}} gap={1} mb={2} flexShrink={0}>
                <Typography variant="h6" fontWeight={700} color="text.primary">GL Category</Typography>
                <Stack direction={{xs: 'column', sm: 'row'}} spacing={1} alignItems={{xs: 'stretch', sm: 'center'}}>
                    <TextField size="small" label="Filter Code" value={filterCode} onChange={e => {
                        setFilterCode(e.target.value);
                    }}/>
                    <TextField size="small" label="Filter Name(EN)" value={filterENName} onChange={e => {
                        setFilterENName(e.target.value);
                    }}/>
                    <TextField size="small" label="Filter Name(ZH)" value={filterZHName} onChange={e => {
                        setFilterZHName(e.target.value);
                    }}/>
                    <TextField select size="small" label="Type" value={filterType} onChange={e => {
                        setFilterType(e.target.value);
                    }} sx={{minWidth: 160}}>
                        <MenuItem value="">All</MenuItem>
                        <MenuItem value="ASSET">ASSET</MenuItem>
                        <MenuItem value="LIABILITY">LIABILITY</MenuItem>
                        <MenuItem value="EQUITY">EQUITY</MenuItem>
                        <MenuItem value="REVENUE">REVENUE</MenuItem>
                        <MenuItem value="EXPENSE">EXPENSE</MenuItem>
                        <MenuItem value="ADJUSTMENT">ADJUSTMENT</MenuItem>
                    </TextField>
                    {canRead && (
                        <Button
                            variant="outlined"
                            sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                            onClick={handleExport}
                            disabled={exporting || importing}
                        >
                            {exporting ? 'Exporting…' : 'Export'}
                        </Button>
                    )}
                    {(canCreate || canUpdate) && (
                        <>
                            <Button
                                variant="outlined"
                                sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                                onClick={handleImportClick}
                                disabled={exporting || importing}
                            >
                                {importing ? 'Importing…' : 'Import'}
                            </Button>
                            <input
                                ref={importInputRef}
                                type="file"
                                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
                                hidden
                                onChange={handleImportFile}
                            />
                        </>
                    )}
                    {canCreate && (
                    <Button variant="contained" color="secondary"
                            sx={{textTransform: 'none', fontWeight: 600, borderRadius: theme.shape.borderRadius}}
                            onClick={() => {
                                setEditId(undefined);
                                setDialogOpen(true);
                            }}>ADD</Button>
                    )}
                </Stack>
            </Stack>
            <TableContainer
                component={Paper}
                sx={{
                    flex: 1,
                    minHeight: 0,
                    overflow: 'auto',
                    boxShadow: theme.shadows[2],
                    bgcolor: 'background.paper',
                }}
            >
                <Table
                    size="small"
                    stickyHeader
                    sx={{
                        minWidth: 960,
                        tableLayout: 'fixed',
                        '& .MuiTableCell-stickyHeader': {
                            bgcolor: 'background.paper',
                            zIndex: 3,
                            borderBottom: `1px solid ${theme.palette.divider}`,
                        },
                    }}
                >
                    <TableHead>
                        <TableRow>
                            <TableCell sx={{width: 56, p: 0}}></TableCell>
                            <TableCell sx={{width: 140, fontWeight: 700}}>Code</TableCell>
                            <TableCell sx={{width: 220, fontWeight: 700}}>Name (EN)</TableCell>
                            <TableCell sx={{width: 200, fontWeight: 700}}>Name (ZH)</TableCell>
                            <TableCell sx={{width: 140, fontWeight: 700}}>FS Code</TableCell>
                            <TableCell sx={{width: 120, fontWeight: 700}}>Type</TableCell>
                            <TableCell sx={{width: 90, fontWeight: 700}} align="center">Active</TableCell>
                            <TableCell sx={{width: 200, fontWeight: 700}} align="center">Action</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {(() => {
                            const flat = flattenTree(pagedRoots || [], getRowId);
                            const visible = getVisibleTreeRows(flat, openRowIds);
                            if (!visible.length) {
                                return (
                                    <TableRow>
                                        <TableCell colSpan={8} align="center">No data</TableCell>
                                    </TableRow>
                                );
                            }
                            return visible.map((r: any) => (
                                <React.Fragment key={r.categoryId}>
                                    <TableRow hover sx={{'& > *': {borderBottom: 'unset'}}}>
                                        <TableCell sx={{width: 56, p: 0.5, ...treeLeadingCellSx(r.level)}}>
                                            <TreeExpandCell
                                                hasChildren={!!(r.children && r.children.length > 0)}
                                                isOpen={openRowIds.includes(r.categoryId)}
                                                onToggle={() => handleToggleRow(r.categoryId)}
                                            />
                                        </TableCell>
                                        <TableCell sx={{width: 140, ...treeLeadingCellSx(r.level)}}>{r.code || '-'}</TableCell>
                                        <TableCell sx={{width: 220, fontWeight: 500, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>
                                            {r.nameEn || '-'}
                                        </TableCell>
                                        <TableCell sx={{width: 200, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}}>{r.nameZh || '-'}</TableCell>
                                        <TableCell sx={{width: 140, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis'}} title={r.fsCode || ''}>
                                            {r.fsCode || '-'}
                                        </TableCell>
                                        <TableCell sx={{width: 120}}>
                                            {r.type ? <Chip size="small" color={typeColor(r.type) as any} label={r.type}/> : '-'}
                                        </TableCell>
                                        <TableCell sx={{width: 90}} align="center">{(r as any).isActive === 1 ? 'Yes' : 'No'}</TableCell>
                                        <TableCell sx={{width: 200}} align="center">
                                            <Stack direction="row" spacing={1} justifyContent="center">
                                                {canRead && (
                                                <Button size="small" variant="outlined" onClick={() => {
                                                    setEditId(r.categoryId);
                                                    setDialogOpen(true);
                                                }}>Detail</Button>
                                                )}
                                                {canRemove && (
                                                <Button size="small" color="error" variant="outlined"
                                                        onClick={() => handleDelete(r.categoryId)}>Delete</Button>
                                                )}
                                            </Stack>
                                        </TableCell>
                                    </TableRow>
                                    {r.children && r.children.length > 0 && (
                                        <TableRow>
                                            <TableCell style={{paddingBottom: 0, paddingTop: 0}} colSpan={8}>
                                                <Collapse in={openRowIds.includes(r.categoryId)} timeout="auto" unmountOnExit>
                                                    <Box sx={{bgcolor: 'background.default'}} />
                                                </Collapse>
                                            </TableCell>
                                        </TableRow>
                                    )}
                                </React.Fragment>
                            ));
                        })()}
                    </TableBody>
                </Table>
            </TableContainer>
            <TablePagination
                component="div"
                count={total}
                page={page}
                onPageChange={(_, p) => setPage(p)}
                rowsPerPage={rowsPerPage}
                onRowsPerPageChange={(e) => {
                    setRowsPerPage(parseInt(e.target.value, 10));
                    setPage(0);
                }}
                rowsPerPageOptions={[10, 25, 50, 100]}
                sx={{flexShrink: 0}}
            />
            <GlCategoryDetail
                open={dialogOpen}
                categoryId={editId}
                onClose={(p) => {
                    setDialogOpen(false);
                    setEditId(undefined);
                    if (p?.message) setSnackbar({open: true, message: p.message, severity: p.severity || 'success'});
                    if (p?.refreshed) fetchData();
                }}
            />
            <Snackbar
                open={snackbar.open}
                autoHideDuration={3000}
                onClose={() => setSnackbar({...snackbar, open: false})}
            >
                <Alert severity={snackbar.severity} onClose={() => setSnackbar({...snackbar, open: false})}>
                    {snackbar.message}
                </Alert>
            </Snackbar>
        </Box>
    );
};

export default GlCategoryList;
